import { useCallback, useMemo, useState } from "react";
import { VelavuAPI, VelavuDevice, VelavuSite } from "velavu-js-api";
import { useGlobalEmitter } from "../emitter/emitter-context";
import { getFloorTarget } from "../helper/floor-helper";

export enum PinGatewayStep {
	Warning,
	Position,
}

export interface PinGatewayState {
	selection: VelavuDevice | null;
	step: PinGatewayStep;
	setStep: (step: PinGatewayStep) => void;
	location: [number, number] | null;
	setLocation: (location: [number, number] | null) => void;

	loading: boolean;

	setSelection: (selection: VelavuDevice | null) => void;
	unpin: (device: VelavuDevice) => void;
	submit: () => void;
}

/**
 * Manages the state for pinning the location of a gateway device
 * @param sites The available sites
 * @param activeSiteID The ID of the site to which to pin this gateway, or undefined if no site is active
 * @param floorOverride A mapping of site IDs to their user-selected floor ID
 * @param updateCallback A callback invoked with the new device when the device is updated
 */
export function usePinGatewayState(
	sites: readonly VelavuSite[] | undefined,
	activeSiteID: string | undefined,
	floorOverride: Readonly<{ [siteID: string]: string | undefined }>,
	updateCallback?: (device: VelavuDevice) => void,
): PinGatewayState {
	const emitter = useGlobalEmitter();

	const [selection, setSelection] = useState<VelavuDevice | null>(null);
	const [step, setStep] = useState<PinGatewayStep>(PinGatewayStep.Warning);
	const [location, setLocation] = useState<[number, number] | null>(null);
	const [loading, setLoading] = useState(false);

	const setSelectionWrapper = useCallback(
		(newSelection: VelavuDevice | null) => {
			setSelection(newSelection);
			setLocation(null);
			setStep(PinGatewayStep.Warning);
		},
		[setSelection, setLocation],
	);

	//Resolve the floor linked to the floor picker on the map
	const activeFloorID = useMemo((): string | undefined => {
		if (activeSiteID === undefined) {
			return undefined;
		}

		const activeSite = sites?.find((site) => site.id === activeSiteID);
		if (activeSite === undefined) {
			return undefined;
		}

		return getFloorTarget(activeSite.floors, floorOverride[activeSite.id])
			?.id;
	}, [sites, activeSiteID, floorOverride]);

	const submit = useCallback(async () => {
		if (loading || selection === null || location === null) {
			return;
		}

		setLoading(true);
		let updatedDevice: VelavuDevice;
		try {
			updatedDevice = await VelavuAPI.devices.modifySpecificDevice(
				selection.id,
				{
					location: {
						floor_id: activeFloorID,
						coordinates: location,
						locked: true,
					},
				},
			);
		} catch (error) {
			console.warn("Failed to update device location", error);
			return;
		} finally {
			setLoading(false);
		}

		setSelectionWrapper(null);
		emitter.emit("device", {
			id: updatedDevice.id,
			data: updatedDevice,
		});
		updateCallback?.(updatedDevice);
	}, [
		loading,
		selection,
		location,
		activeFloorID,
		setSelectionWrapper,
		emitter,
		updateCallback,
	]);

	const unpin = useCallback(
		async (device: VelavuDevice) => {
			if (loading) {
				return;
			}

			setLoading(true);
			let updatedDevice: VelavuDevice;
			try {
				updatedDevice = await VelavuAPI.devices
					.removeSpecificDeviceLocation(device.id)
					.then(() => ({
						...device,
						location: undefined,
					}));
			} catch (error) {
				console.warn("Failed to unpin device", error);
				return;
			} finally {
				setLoading(false);
			}

			emitter.emit("device", {
				id: updatedDevice.id,
				data: updatedDevice,
			});
			updateCallback?.(updatedDevice);
		},
		[loading, emitter, updateCallback],
	);

	return useMemo(
		(): PinGatewayState => ({
			selection,
			step,
			setStep,
			location,
			setLocation,
			loading,
			setSelection: setSelectionWrapper,
			unpin,
			submit,
		}),
		[
			selection,
			step,
			setStep,
			location,
			setLocation,
			loading,
			setSelectionWrapper,
			unpin,
			submit,
		],
	);
}
